import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, TextField, InputAdornment, IconButton, Skeleton } from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { agentsService } from '../services/agentsService';

const ISODate = (d) => new Date(d).toLocaleString();

const AgentSessionsTab = ({ agentId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => new Date(Date.now() - 24*60*60*1000).toISOString().slice(0,16));
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0,16));

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      // Para a primeira versão, usamos summary e filtramos client-side por data
      const res = await agentsService.getSessionsSummary(agentId, 50);
      const items = res?.sessions || [];
      setSessions(items);
      setSelected(items[0] || null);
    } catch (e) {
      setError('Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agentId]);

  const filtered = useMemo(() => {
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    return (sessions || []).filter(s => {
      const t = new Date(s.last_message_at || s.created_at);
      return (!from || t >= from) && (!to || t <= to);
    });
  }, [sessions, dateFrom, dateTo]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 2 }}>
      {/* Lista esquerda */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label="De"
            type="datetime-local"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="datetime-local"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <IconButton onClick={load} title="Atualizar">
            <RefreshIcon />
          </IconButton>
        </Box>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={`s-${i}`} variant="rectangular" height={56} sx={{ borderRadius: 1, mb: 1 }} />
          ))
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhuma conversa no período.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filtered.map((s, i) => (
              <Paper
                key={`${s.end_user_id}-${s.thread_id}-${i}`}
                variant={selected === s ? 'elevation' : 'outlined'}
                elevation={selected === s ? 1 : 0}
                onClick={() => setSelected(s)}
                sx={{ p: 1, cursor: 'pointer', borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.end_user_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ISODate(s.last_message_at || s.created_at)} • canal: {s.channel || 'api'} • msgs: {s.message_count || 0}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* Detalhe direita */}
      <Paper variant="outlined" sx={{ p: 2, minHeight: 360 }}>
        {!selected ? (
          <Typography variant="body2" color="text.secondary">Selecione uma conversa para visualizar.</Typography>
        ) : (
          <SessionMessagesView session={selected} />
        )}
      </Paper>
    </Box>
  );
};

const SessionMessagesView = ({ session }) => {
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        setLoading(true);
        setError(null);
        const msgs = await agentsService.getSessionMessages(session.end_user_id, session.thread_id, 200);
        setMessages(msgs);
      } catch (e) {
        setError('Erro ao carregar mensagens');
      } finally {
        setLoading(false);
      }
    };
    if (session) loadMessages();
  }, [session]);

  if (loading) {
    return (
      <Box>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={`m-${i}`} variant="rectangular" height={40} sx={{ borderRadius: 1, mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) return <Typography variant="body2" color="error">{error}</Typography>;
  if (!messages || messages.length === 0) return <Typography variant="body2" color="text.secondary">Sem mensagens nesta conversa.</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {messages.map((m, i) => (
        <Paper key={`msg-${i}`} variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {ISODate(m.created_at || m.timestamp)} • {m.role || m.sender || 'user'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {m.content || m.text || ''}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default AgentSessionsTab;


